import { lazy } from 'react';
import { Redirect } from 'react-router';
import AuthLayout from '../layout/auth-layout';
import MainLayout from '../layout/main-layout';
import paths from './paths';
import { needAuth, needOrgId, needRedirectCauseHasOrgId, needUnAuth } from './router-middleware';

const ImportParticipantByExcel = lazy(() => import('../pages/import-participant'));
const ImportParticipantResult = lazy(() => import('../pages/import-participant-result'));
const ImportRemittanceByExcel = lazy(() => import('../pages/import-remittance'));
const ImportRemittanceResult = lazy(() => import('../pages/import-remittance-result'));
const ImportROCByExcel = lazy(() => import('../pages/import-roc'));
const ImportROCResult = lazy(() => import('../pages/import-roc-result'));
const ImportShiftAsyncResult = lazy(() => import('../pages/import-service-delivery-async-result'));
const Products = lazy(() => import('../pages/products'));
const NdisNotifications = lazy(() => import('../pages/ndis-notifications'));
const Contacts = lazy(() => import('../pages/contacts'));
const CategoryY = lazy(() => import('../pages/categories/category-y'));
const CategoryX = lazy(() => import('../pages/categories/category-x'));
const Registrations = lazy(() => import('../pages/registrations'));
const Remittance = lazy(() => import('../pages/remittance'));
const InvoiceBatchHistory = lazy(() => import('../pages/invoice-batch-history'));
const InvoiceBatchDetails = lazy(() => import('../pages/invoice-details'));
const InvoiceInError = lazy(() => import('../pages/invoices-in-error'));
const Profile = lazy(() => import('../pages/profile'));
const Welcome = lazy(() => import('../pages/welcome'));
const Login = lazy(() => import('../pages/login'));
const Lock = lazy(() => import('../pages/lock'));
const Register = lazy(() => import('../pages/register'));
const ForgetPassword = lazy(() => import('../pages/forget-password'));
const Callback = lazy(() => import('../pages/callback'));
const TermsAndConditions = lazy(() => import('../pages/terms-and-conditions'));
const Test = lazy(() => import('../pages/test'));
const Settings = lazy(() => import('../pages/settings'));
const SelectOrg = lazy(() => import('../pages/select-organisation'));
const Participants = lazy(() => import('../pages/participants'));
const Budget = lazy(() => import('../pages/participant-budget'));
const Analytics = lazy(() => import('../pages/analytics'));
const ServiceBookings = lazy(() => import('../pages/service-bookings'));
const Participant360View = lazy(() => import('../pages/participant-360-view'));
const Integration = lazy(() => import('../pages/integration'));
const RosterOfCare = lazy(() => import('../pages/roster-of-care'));
const ServiceDelivery = lazy(() => import('../pages/service-delivery'));
const ReadyToClaim = lazy(() => import('../pages/ready-to-claim'));
const RecoveryClaim = lazy(() => import('../pages/recovery-claim'));
const SuccessfulClaim = lazy(() => import('../pages/successful-claims'));
const SuccessfulClaimsNewGrid = lazy(() => import('../pages/successful-claim-new-grid'));
const PaymentCancellation = lazy(() => import('../pages/payment-cancellation'));
const ClaimBatchHistory = lazy(() => import('../pages/claim-batch-history'));
const ClaimBatchDetail = lazy(() => import('../pages/claim-batch-detail'));
const ClaimsInError = lazy(() => import('../pages/claims-in-error'));
const ReadyToInvoice = lazy(() => import('../pages/ready-to-invoice'));
const IssuedInvoices = lazy(() => import('../pages/Issued-invoices'));
const CreateServiceBooking = lazy(() => import('../pages/create-service-booking'));
const SupportAtHomeCareRecipient360 = lazy(
  () => import('../pages/sah/sah-care-recipient-360/care-recipient-360-view'),
);
const SupportAtHomeCareRecipients = lazy(() => import('../pages/sah/sah-care-recipients'));
const SupportAtHomeTransactions = lazy(() => import('../pages/sah/sah-transactions'));
const SupportAtHomeReadyToClaim = lazy(() => import('../pages/sah/sah-ready-to-claim'));
const SupportAtHomeClaims = lazy(() => import('../pages/sah/sah-claims'));
const SupportAtHomeClaimHistory = lazy(() => import('../pages/sah/sah-claim-history'));
const SupportAtHomeReadyToInvoice = lazy(() => import('../pages/sah/sah-ready-to-invoice'));
const SupportAtHomeProviders = lazy(() => import('../pages/sah/sah-providers'));
const SupportAtHomeTransactionImportHistory = lazy(
  () => import('../pages/sah/sah-transaction-import-history'),
);
const SupportAtHomeImportTransactions = lazy(() => import('../pages/sah/sah-import-transactions'));

const routes = [
  {
    path: '/app/:path?',
    layout: MainLayout,
    middleware: [needAuth],
    children: [
      {
        path: paths.analytics,
        component: () => <Analytics key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.profile,
        component: () => <Profile key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.lock,
        component: () => <Lock key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },

      {
        path: paths.registrations,
        component: () => <Registrations key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.products,
        component: () => <Products key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.categoryX,
        component: () => <CategoryX key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.categoryY,
        component: () => <CategoryY key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.financialContact,
        component: () => <Contacts key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.ndisNotifications,
        component: () => <NdisNotifications key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/app/404',
        component: lazy(() => import('../pages/error')),
      },
      { path: '*', component: () => <Redirect to="/app/404" /> },
    ],
  },
  {
    path: '/ndis/:path?',
    layout: MainLayout,
    middleware: [needAuth],
    children: [
      {
        path: paths.serviceBookings,
        component: () => <ServiceBookings key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.participant360View,
        component: () => <Participant360View key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.participants,
        component: () => <Participants key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.rosterOfCare,
        component: () => <RosterOfCare key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.serviceDelivery,
        component: () => <ServiceDelivery key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.readyToClaim,
        component: () => <ReadyToClaim key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.readyToInvoice,
        component: () => <ReadyToInvoice key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.recoveryClaim,
        component: () => <RecoveryClaim key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.successfulClaims,
        component: () => <SuccessfulClaim key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.successfulClaimsNewGrid,
        component: () => <SuccessfulClaimsNewGrid key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.paymentCancellation,
        component: () => <PaymentCancellation key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.claimBatchHistory,
        component: () => <ClaimBatchHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.claimBatchDetail,
        component: () => <ClaimBatchDetail key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.claimsInError,
        component: () => <ClaimsInError key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.issuedInvoices,
        component: () => <IssuedInvoices key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoiceBatchHistory,
        component: () => <InvoiceBatchHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.remittance,
        component: () => <Remittance key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoiceDetails,
        component: () => <InvoiceBatchDetails key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.invoicesInError,
        component: () => <InvoiceInError key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.budget,
        component: () => <Budget key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.createServiceBooking,
        component: () => <CreateServiceBooking key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importParticipantByExcel,
        component: () => <ImportParticipantByExcel key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importParticipantResult,
        component: () => <ImportParticipantResult key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importROCByExcel,
        component: () => <ImportROCByExcel key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importROCResult,
        component: () => <ImportROCResult key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importRemittanceByExcel,
        component: () => <ImportRemittanceByExcel key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importRemittanceResult,
        component: () => <ImportRemittanceResult key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.importShiftAsyncResult,
        component: () => <ImportShiftAsyncResult key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.integration,
        component: () => <Integration key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      // {
      //   path: '/app',
      //   component: RootRedirect,
      //   middleware: [needAuth],
      //   exact: true,
      // },
      {
        path: '/ndis/404',
        component: lazy(() => import('../pages/error')),
      },
      { path: '*', component: () => <Redirect to="/ndis/404" /> },
    ],
  },
  {
    path: '/support-at-home/:path?',
    layout: MainLayout,
    middleware: [needAuth],
    children: [
      {
        path: paths.supportAtHomeCareRecipient360,
        component: () => <SupportAtHomeCareRecipient360 key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeCareRecipients,
        component: () => <SupportAtHomeCareRecipients key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeTransactions,
        component: () => <SupportAtHomeTransactions key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeReadyToClaim,
        component: () => <SupportAtHomeReadyToClaim key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeClaims,
        component: () => <SupportAtHomeClaims key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeClaimHistory,
        component: () => <SupportAtHomeClaimHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeReadyToInvoice,
        component: () => <SupportAtHomeReadyToInvoice key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeProviders,
        component: () => <SupportAtHomeProviders key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeTransactionImportHistory,
        component: () => <SupportAtHomeTransactionImportHistory key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: paths.supportAtHomeImportTransactions,
        component: () => <SupportAtHomeImportTransactions key={performance.now()} />,
        middleware: [needAuth, needOrgId],
      },
      {
        path: '/support-at-home/404',
        component: lazy(() => import('../pages/error')),
      },
      { path: '*', component: () => <Redirect to="/support-at-home/404" /> },
    ],
  },
  {
    path: '/',
    layout: AuthLayout,
    children: [
      {
        path: paths.login,
        component: () => <Login />,
        middleware: [needUnAuth],
      },
      {
        path: paths.callback,
        component: () => <Callback />,
        // middleware: [needUnAuth], // TODO
      },
      {
        path: paths.register,
        component: () => <Register />,
        middleware: [needUnAuth],
      },
      {
        path: paths.forgetPassword,
        component: () => <ForgetPassword />,
        middleware: [needUnAuth],
      },
      {
        path: paths.welcome,
        component: () => <Welcome />,

        middleware: [needAuth, needRedirectCauseHasOrgId],
      },
      {
        path: paths.termsAndConditions,
        component: () => <TermsAndConditions />,
        middleware: [needAuth],
      },
      {
        path: paths.selectOrg,
        component: () => <SelectOrg />,
        middleware: [needAuth],
      },
      {
        path: '/',
        component: () => <Redirect to={paths.login} />,
      },
      {
        path: '/404',
        component: lazy(() => import('../pages/error')),
      },
      { path: '*', component: () => <Redirect to="/404" /> },
    ],
  },
];

export default routes;
